/**
 * utilities/passwordSuggestion.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The password the "Set login" dialog proposes for a vendor, and the rules it
 * is checked against before the server sees it.
 *
 * SHAPE: Word-1234-Word, for example "Mango-7429-River". It is read out over
 * the phone and typed from a WhatsApp message on a cheap handset, so it avoids
 * everything that gets mistyped: no 0 or 1 (O and l), no word starting with I
 * (l), one capital per word in the obvious place, a hyphen rather than a
 * symbol the keyboard hides on a second page.
 *
 * STRENGTH: 256 words x 8^4 digit runs x 256 words = 2^28 combinations, drawn
 * from crypto.getRandomValues (never Math.random). With bcrypt at cost 12 and
 * the sign-in rate limit that is far beyond guessing online, and it is a
 * hand-over password the vendor is told to change once they are in.
 *
 * The RULES mirror services/auth/vendorLoginSetter.js `adminSetPasswordProblem`
 * on the server, which has the final say; this copy only lets the dialog say
 * what is wrong before the admin presses Save.
 */

// 256 entries, so a byte picks one with no modulo bias.
export const WORDS = Object.freeze([
  "Acorn", "Amber", "Anchor", "Apple", "Apron", "Arrow", "Aspen", "Atlas",
  "Avocado", "Badge", "Bagel", "Bamboo", "Banana", "Banjo", "Barley", "Basil",
  "Basket", "Beacon", "Berry", "Bison", "Blossom", "Bongo", "Breeze", "Brick",
  "Bridge", "Bucket", "Buffalo", "Butter", "Cabin", "Cactus", "Camel", "Candle",
  "Canoe", "Canyon", "Carrot", "Cashew", "Castle", "Cedar", "Cello", "Cherry",
  "Cinnamon", "Clover", "Cobalt", "Cocoa", "Coconut", "Comet", "Copper", "Coral",
  "Cotton", "Coyote", "Crane", "Crystal", "Cupcake", "Daisy", "Delta", "Desert",
  "Dolphin", "Domino", "Dragon", "Drum", "Eagle", "Ember", "Emerald", "Falcon",
  "Feather", "Fennel", "Fern", "Fiddle", "Figure", "Flame", "Flute", "Forest",
  "Fossil", "Fountain", "Fox", "Galaxy", "Garden", "Garlic", "Gazelle", "Ginger",
  "Giraffe", "Glacier", "Granite", "Grape", "Guava", "Guitar", "Hammer", "Harbor",
  "Harp", "Hazel", "Helmet", "Heron", "Hibiscus", "Hippo", "Honey", "Horizon",
  "Hornbill", "Jacket", "Jade", "Jaguar", "Jasmine", "Jelly", "Jetty", "Jungle",
  "Kayak", "Kernel", "Kettle", "Kiwi", "Koala", "Ladder", "Lagoon", "Lantern",
  "Lemon", "Leopard", "Lily", "Lime", "Lion", "Llama", "Lotus", "Lychee",
  "Magnet", "Mango", "Maple", "Marble", "Meadow", "Melon", "Meteor", "Mint",
  "Mirror", "Monsoon", "Moose", "Mosaic", "Muffin", "Nectar", "Needle", "Nest",
  "Noodle", "Nutmeg", "Nugget", "Oasis", "Olive", "Onion", "Orange", "Orbit",
  "Orchid", "Otter", "Paddle", "Palm", "Panda", "Papaya", "Parrot", "Peach",
  "Peanut", "Pearl", "Pebble", "Pelican", "Pepper", "Piano", "Pickle", "Pigeon",
  "Pillow", "Pine", "Planet", "Plum", "Pocket", "Pony", "Poppy", "Puffin",
  "Pumpkin", "Quail", "Quilt", "Rabbit", "Radish", "Rainbow", "Raven", "Reef",
  "Ribbon", "River", "Robin", "Rocket", "Rose", "Ruby", "Saddle", "Saffron",
  "Sage", "Sail", "Salmon", "Sandal", "Sapphire", "Savanna", "Scarf", "Sesame",
  "Shell", "Silver", "Sky", "Sparrow", "Spice", "Spruce", "Squash", "Star",
  "Stone", "Sugar", "Summit", "Sunset", "Swan", "Tamarind", "Tango", "Teapot",
  "Thunder", "Tiger", "Timber", "Tomato", "Topaz", "Torch", "Toucan", "Tulip",
  "Turtle", "Umbrella", "Valley", "Vanilla", "Velvet", "Violet", "Voyage", "Waffle",
  "Walnut", "Walrus", "Wave", "Whistle", "Willow", "Window", "Winter", "Wren",
  "Yak", "Yellow", "Yogurt", "Zebra", "Zenith", "Zest", "Zinc", "Zucchini",
  "Ukulele", "Urchin", "Unicorn", "Utopia", "Pilot", "Cricket", "Kite", "Sorghum",
  "Biscuit", "Daffodil", "Gecko", "Hamster", "Nimbus", "Pretzel", "Raisin", "Tortoise",
]);

// No 0 or 1: they read as O and l.
const DIGITS = "23456789";

const cryptoSource = (given) => {
  const c =
    given ||
    (typeof window !== "undefined" ? window.crypto || window.msCrypto : undefined) ||
    globalThis.crypto;
  if (!c || typeof c.getRandomValues !== "function") {
    // Refuse rather than fall back to Math.random: a guessable suggestion is
    // worse than none, and the admin can still type one.
    throw new Error("Secure random numbers are not available in this browser.");
  }
  return c;
};

/**
 * A fresh suggestion, e.g. "Mango-7429-River". Two different words.
 * @param {{getRandomValues: Function}} [source] injectable for tests
 */
export const suggestVendorPassword = (source) => {
  const random = cryptoSource(source);
  const bytes = new Uint8Array(6);
  random.getRandomValues(bytes);
  const first = WORDS[bytes[0]];
  let second = WORDS[bytes[1]];
  if (second === first) second = WORDS[(bytes[1] + 1) % WORDS.length];
  // Each digit uses the low 3 bits of its byte: 8 digits, no bias.
  const digits = [2, 3, 4, 5].map((i) => DIGITS[bytes[i] & 7]).join("");
  return `${first}-${digits}-${second}`;
};

export const ADMIN_SET_PASSWORD_MIN_LENGTH = 10;

// bcrypt reads 72 BYTES. Counted without TextEncoder, which some test and
// older embedded browsers lack: each %XX in the URI encoding is one byte.
const utf8Length = (value) => encodeURIComponent(value).replace(/%[0-9A-F]{2}/gi, "x").length;

/**
 * The sentence to show under the password field, or null when it is fine.
 * Mirrors the server's adminSetPasswordProblem.
 */
export const adminPasswordProblem = (password, { email, minLength = ADMIN_SET_PASSWORD_MIN_LENGTH } = {}) => {
  if (typeof password !== "string" || !password.length) return "Enter a password for the vendor.";
  if (password !== password.trim()) return "Remove the space at the start or end of the password.";
  if (password.length < minLength) return `Use at least ${minLength} characters.`;
  if (utf8Length(password) > 72) return "That password is too long. Keep it under 70 characters.";
  if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) return "Use letters and at least one number.";
  const local = String(email || "").split("@")[0].toLowerCase();
  if (local.length >= 4 && password.toLowerCase().includes(local)) {
    return "The password should not contain the login email.";
  }
  return null;
};

export default suggestVendorPassword;
